import type { FocusEvent } from 'react'
import { DatePicker } from '@/shared/components'
import type { EnglishTestQuestionDto } from '@/api/generated/models'
import type { QuestionAnswerValue } from './types'
import {
  byDisplayOrder,
  isDateEntryQuestion,
  isFillInTheBlankQuestion,
  isPlainTextEntryQuestion,
  resolveQuestionImageUrl
} from './questionUtils'

type EnglishTestQuestionCardProps = {
  question: EnglishTestQuestionDto
  questionIndex: number
  selectedAnswer: QuestionAnswerValue
  onAnswerSelect: (questionIndex: number, optionIndex: number) => void
  onTextAnswerChange: (questionIndex: number, value: string) => void
  onTextAnswerBlur: (questionIndex: number, event: FocusEvent<HTMLInputElement>) => void
  onDateAnswerChange: (question: EnglishTestQuestionDto, questionIndex: number, value: string) => void
}

export function EnglishTestQuestionCard({
  question,
  questionIndex,
  selectedAnswer,
  onAnswerSelect,
  onTextAnswerChange,
  onTextAnswerBlur,
  onDateAnswerChange
}: EnglishTestQuestionCardProps) {
  const options = [...(question.answers ?? [])].sort(byDisplayOrder)
  const isFillInBlank = isFillInTheBlankQuestion(question)
  const isDateEntry = isDateEntryQuestion(question)
  const isPlainTextEntry = isPlainTextEntryQuestion(question)
  const textAnswer = selectedAnswer?.type === 'text' ? selectedAnswer.value : ''
  const questionImageUrl = resolveQuestionImageUrl(question.imageUrl)

  return (
    <div
      key={question.id ?? `${question.questionText}-${questionIndex}`}
      data-question-index={questionIndex}
      tabIndex={-1}
      className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
    >
      <div className="mb-4 flex items-start gap-3">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
          {questionIndex + 1}
        </span>
        <div className="space-y-2 pt-1">
          <p className="text-base font-semibold leading-6 text-slate-900">
            {question.questionText ?? 'Untitled question'}
          </p>

          {question.readingPassage && (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-600">
              {question.readingPassage}
            </div>
          )}

          {questionImageUrl && (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <img
                src={questionImageUrl}
                alt={question.questionText ?? `Question ${questionIndex + 1} illustration`}
                className="max-h-72 w-full object-contain bg-slate-100"
              />
            </div>
          )}
        </div>
      </div>

      {isFillInBlank || isPlainTextEntry ? (
        <div className="space-y-2">
          <label
            htmlFor={`english-question-${questionIndex}`}
            className="block text-sm font-semibold text-slate-700"
          >
            Your answer
          </label>
          <input
            id={`english-question-${questionIndex}`}
            type={isPlainTextEntry ? 'email' : 'text'}
            value={textAnswer}
            onChange={event => onTextAnswerChange(questionIndex, event.target.value)}
            onBlur={event => onTextAnswerBlur(questionIndex, event)}
            placeholder={isPlainTextEntry ? 'Enter your email' : 'Type your answer here'}
            className="enrollment-field-control"
          />
        </div>
      ) : isDateEntry ? (
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-slate-700">Your answer</label>
          <DatePicker
            value={textAnswer}
            onChange={value => onDateAnswerChange(question, questionIndex, value)}
            placeholder="Select a date"
            popperClassName="datepicker-popper english-test-datepicker-popper"
          />
        </div>
      ) : (
        <div className="grid gap-3">
          {options.map((option, optionIndex) => {
            const isSelected =
              selectedAnswer?.type === 'option' && selectedAnswer.optionIndex === optionIndex

            return (
              <label
                key={option.id ?? `${option.answerText}-${optionIndex}`}
                className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition-all duration-200 ${
                  isSelected
                    ? 'border-primary bg-primary/[0.08] shadow-sm'
                    : 'border-white bg-white hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name={`english-question-${questionIndex}`}
                  checked={isSelected}
                  onChange={() => onAnswerSelect(questionIndex, optionIndex)}
                  className="mt-1 shrink-0"
                />
                <span className="text-sm leading-6 text-slate-700">{option.answerText}</span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
